// ML001-F0218 / F0222 / F0223 (WebUI #169). Pure model for the member-page
// Credit notes tag, coded to Spec core-ledger-account-gateway 1.9.0: rows are
// CreditNote (kind credit_note) or CreditNoteDraft (kind credit_note_draft),
// amounts are integer minor units, and archive fields are optional on the wire.
// No React here, so each rule is unit-tested directly.

// Reads need billing_transactions_view; writes need ledger_manage (Spec 1.9.0,
// the permission each operation declares). Absent or non-true means no access.
export const CREDIT_NOTE_VIEW_KEY = 'billing_transactions_view';
export const CREDIT_NOTE_MANAGE_KEY = 'ledger_manage';

export const canViewCreditNotes = (permissions) => permissions?.[CREDIT_NOTE_VIEW_KEY] === true;
export const canManageCreditNotes = (permissions) => permissions?.[CREDIT_NOTE_MANAGE_KEY] === true;

export const isDraft = (row) => row?.kind === 'credit_note_draft';
export const rowId = (row) => (isDraft(row) ? row?.credit_note_draft_id : row?.credit_note_id);

// Minor units to a pound amount.
export const fromMinor = (minor) => Number(minor || 0) / 100;

// Archived = an issued note the Ledger archived (F0222), a cancelled note (its
// status is derived from the cancelling note), or a discarded draft.
// A fully used note (ML001-F0213, T-LED-CALLOC-009): an issued note whose REMAINING balance is zero. A cancelling note
// (it has `cancels`) carries no balance of its own and is never counted as used.
export const isUsedUp = (row) => !isDraft(row) && row?.status !== 'cancelled' && !row?.cancels
  && Number(row?.amount_minor) > 0 && Number(row?.available_minor) === 0;

export const isArchivedRow = (row) => {
  if (isDraft(row)) return row?.status === 'discarded';
  // CreditNote.lifecycle (Spec 1.12.0, ML001-F0222) is the Ledger's own Active / Archived fact when it is present; an older Ledger omits it
  // and the derived rules below decide.
  if (row?.lifecycle === 'archived' || row?.lifecycle === 'active') return row.lifecycle === 'archived';
  return row?.archived === true || row?.status === 'cancelled' || Boolean(row?.archive_reason) || isUsedUp(row);
};

export function splitCreditNotes(rows) {
  const list = Array.isArray(rows) ? rows : [];
  return {
    active: list.filter((row) => !isArchivedRow(row)),
    archived: list.filter(isArchivedRow),
  };
}

// The available chip counts issued notes in Active only. A draft is not yet a
// numbered note, so its amount is not available to apply.
export function availableMinorTotal(rows) {
  return (rows || [])
    .filter((row) => !isDraft(row) && !isArchivedRow(row))
    .reduce((sum, row) => sum + Number(row.available_minor || 0), 0);
}

// Final-state pill (§2.60.13). The 1.9.0 contract carries no expired or refunded
// amount, so those pills read without a pound figure rather than an invented one.
export function statusLabel(row) {
  if (row?.status === 'discarded') return 'Discarded';
  if (isDraft(row)) return 'Draft';
  if (row?.status === 'cancelled' || row?.archive_reason === 'cancelled') return 'Cancelled';
  switch (row?.archive_reason) {
    case 'used': return 'Used';
    case 'expired': return 'Expired';
    case 'refunded': return 'Refunded';
    default: return isUsedUp(row) ? 'Used' : 'Issued';
  }
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const toNumber = (value) => (value === '' || value === null || value === undefined ? NaN : Number(value));

// Client-side guard for the issue drawer. The server re-validates; these rules
// only keep the drawer from sending a request it already knows is invalid.
export function validateIssueForm(form) {
  const errors = {};
  const amount = toNumber(form.amount);
  if (!(amount > 0) || Math.abs(amount * 100 - Math.round(amount * 100)) > 1e-6) {
    errors.amount = 'Enter an amount greater than zero, in whole pence.';
  }
  if (!String(form.reason || '').trim()) errors.reason = 'Give a reason for the credit note.';
  if (!DATE_RE.test(form.issueDate || '')) errors.issueDate = 'Enter the issue date.';
  return errors;
}

export const emptyIssueForm = (today) => ({
  amount: '',
  reason: '',
  issueDate: today,
  originalInvoice: '',
});

// Body for POST /v1/orgs/:orgId/ledger/credit-notes (CreditNoteDraftRequest,
// additionalProperties false). Optional keys are omitted, never sent as null.
export function buildDraftBody(form, userId) {
  const body = {
    user_id: Number(userId),
    amount_minor: Math.round(Number(form.amount) * 100),
    reason: String(form.reason).trim(),
    issue_date: form.issueDate,
  };
  const invoice = String(form.originalInvoice || '').trim();
  if (invoice) body.original_invoice_id = invoice;
  return body;
}

// ML001-F0213: the remaining balance a note can still give, in minor units (the Ledger's available_minor).
export const remainingMinor = (row) => Math.max(0, Number(row?.available_minor) || 0);

// The Ledger's invoice record (src/invoicing/invoiceEngine.js generate(): invoice_id, invoice_number, status, total_amount in POUNDS;
// balance_due in POUNDS once an allocation or payment has written it, see invoiceEngine.js invoiceBalance / creditAllocation.js _caDerive).
// The account read (src/server.js account.summary) returns these raw records, filtered to non-draft/non-discarded.
// What it owes = balance_due when present, else total_amount, the same fallback the Ledger's invoice PDF uses (invoiceEngine.js ~271).
// Converted to minor units here, once, so every caller compares minor with minor.
export function invoiceOwedMinor(invoice) {
  const owed = invoice?.balance_due ?? invoice?.total_amount;
  if (owed === null || owed === undefined || owed === '' || !Number.isFinite(Number(owed))) return 0;
  return Math.round(Number(owed) * 100);
}

// The member's invoices the apply picker offers: not pre-issue (draft/discarded, the Ledger's isIssuedInvoice rule) and still owing
// money. A part-paid invoice (status 'part_paid' after a payment or credit) that still owes is offered too: status 'issued' alone would hide it.
// An invoice without a numeric balance is not offered: the apply cap cannot be enforced without it (fail closed).
const PRE_ISSUE_STATUSES = new Set(['draft', 'discarded']);
export function issuedInvoicesWithBalance(invoices) {
  return (Array.isArray(invoices) ? invoices : []).filter((invoice) => invoice?.invoice_id
    && typeof invoice.status === 'string' && !PRE_ISSUE_STATUSES.has(invoice.status)
    && invoiceOwedMinor(invoice) > 0);
}

// Apply form guard. The amount may not exceed the note's remaining balance nor the invoice's balance due; the reason is required
// on an issued invoice (Spec: LEDGER_REASON_REQUIRED). The Ledger re-validates every one of these.
export function validateApplyForm({ amount, invoiceId, reason }, { remaining, invoiceDue }) {
  const errors = {};
  const pounds = toNumber(amount);
  const minor = Math.round(pounds * 100);
  if (!invoiceId) errors.invoice = 'Choose an invoice.';
  if (!(pounds > 0) || Math.abs(pounds * 100 - minor) > 1e-6) {
    errors.amount = 'Enter an amount greater than zero, in whole pence.';
  } else if (minor > remaining) {
    errors.amount = 'The amount is more than the credit note has left.';
  } else if (minor > invoiceDue) {
    errors.amount = 'The amount is more than the invoice still owes.';
  }
  if (!String(reason || '').trim()) errors.reason = 'Give a reason for applying this credit.';
  return errors;
}

// Body for credit_allocation.apply (TunnelCreditAllocationApplyRequest body, additionalProperties false).
export function buildApplyBody({ creditNoteId, invoiceId, amount, reason }) {
  return {
    credit_note_id: creditNoteId,
    invoice_id: invoiceId,
    amount_minor: Math.round(Number(amount) * 100),
    reason: String(reason).trim(),
  };
}

// M004-F0757 (WebUI #201): Refund CR (Spec core-ledger-account-gateway 1.17.0, credit.refund). The amount is capped at what the
// note still holds (available_minor) and a comment is required; the Ledger enforces the same cap and answers the refusal code.
export function validateRefundForm({ amount, reason }, { available }) {
  const errors = {};
  const pounds = toNumber(amount);
  const minor = Math.round(pounds * 100);
  if (!(pounds > 0) || Math.abs(pounds * 100 - minor) > 1e-6) {
    errors.amount = 'Enter an amount greater than zero, in whole pence.';
  } else if (minor > available) {
    errors.amount = 'The refund is more than the credit note has left.';
  }
  if (!String(reason || '').trim()) errors.reason = 'Give a comment for this refund.';
  return errors;
}

// Body for credit.refund (TunnelCreditRefundRequest body: customer_id, amount_minor, reason; additionalProperties false).
export function buildRefundBody({ customerId, amount, reason }) {
  return {
    customer_id: String(customerId),
    amount_minor: Math.round(Number(amount) * 100),
    reason: String(reason).trim(),
  };
}

// Allocations of one note that are still in force (state active).
export const activeAllocationsFor = (allocations, creditNoteId) => (Array.isArray(allocations) ? allocations : [])
  .filter((allocation) => allocation?.credit_note_id === creditNoteId && allocation?.state === 'active');

export const validateReason = (reason, message = 'Give a reason for this action.') => (String(reason || '').trim() ? null : message);

// M004-F0751 (WebUI #193): the org-wide Credit notes tab. Coded to the pinned Spec CreditNoteListRequest, which filters by `status` and
// `archived` only (no archive_reason, no paging). Fully used and Expired are read from the archived set and filtered here by archive_reason.
// The Ledger never silently cuts a list: it refuses one over 2000 rows, so a 2000-row answer is treated as possibly incomplete (fail closed).
export const CREDIT_NOTE_LIST_CAP = 2000;
export const CN_FILTERS = Object.freeze(['active', 'used', 'expired', 'all']);
export const CN_FILTER_LABELS = Object.freeze({ active: 'Active', used: 'Fully used', expired: 'Expired', all: 'All' });

// Either gate the Ledger applies to reads (billing_transactions_view, or ledger_manage). Teachers and students hold neither.
export const canOpenCreditNotes = (permissions) => canViewCreditNotes(permissions) || canManageCreditNotes(permissions);

// The org-wide list request for a filter. Never carries user_id or target_user_id: member filtering is not this tab's job.
export const orgListRequest = (filter) => {
  if (filter === 'active') return { archived: false };
  if (filter === 'used' || filter === 'expired') return { archived: true };
  return {};
};

// The rows a filter shows, from the set the request returned.
export const rowsForFilter = (rows, filter) => {
  const list = Array.isArray(rows) ? rows : [];
  if (filter === 'used') return list.filter((row) => row?.archive_reason === 'used');
  if (filter === 'expired') return list.filter((row) => row?.archive_reason === 'expired');
  return list;
};

// The unused total: the sum of available_minor over the Active set only. Archived rows are excluded here as well, so the figure never
// includes a note the Ledger has archived, whatever set it is given.
export const unusedTotalMinor = (rows) => (Array.isArray(rows) ? rows : [])
  .filter((row) => row?.archived !== true && !isDraft(row))
  .reduce((sum, row) => sum + Number(row.available_minor || 0), 0);

// A list at the Ledger's cap may be incomplete: the total is then not shown (an incomplete figure is never presented as the total).
export const isListIncomplete = (rows) => (Array.isArray(rows) ? rows.length : 0) >= CREDIT_NOTE_LIST_CAP;

// Expiry shows the Ledger's expires_on only when the 1.12.0 field is present; nothing is computed here (M004-F0751 section 4).
export const expiryText = (row) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(row?.expires_on || ''));
  return match ? `${match[3]}/${match[2]}/${match[1]}` : '—';
};
